<template>
  <div>
    <!-- Table Container Card -->
    <BulksModal user-type="driver" />
    <BulkBlockDrivers />
    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-2">
        <div class="d-flex align-items-center">
          <b-button
            v-if="!isBlockedState && $can('write', 'transaction-bulks') && isJordanianAdmin()"
            class="mr-2"
            variant="success"
            @click="$bvModal.show('transaction-bulks')"
          >
            Transactions Bulks
          </b-button>
          <b-button
            v-if="$can('read', 'blocked-drivers')"
            class="mr-2"
            variant="danger"
            @click="$bvModal.show('drivers-bulk-block')"
          >
            Bulk Block
          </b-button>
          <BulkClearInflightModal
            v-if="$can('write', 'trips')"
            entity-type="drivers"
          />
        </div>
        <card-collapse
          border-variant=" "
          title-class="d-flex justify-content-between"
        >
          <template #card-title>
            <span>Advanced Search</span>
          </template>
          <template>
            <b-form @submit.prevent="applyAdvancedSearch">
              <b-row>
                <b-col :md="isBlockedState ? '3': '4'">
                  <b-form-group>
                    <label>ID:</label>
                    <b-form-input
                      placeholder="Search"
                      type="text"
                      class="d-inline-block"
                      @input="(val) => (searchQuery.id = val)"
                    />
                  </b-form-group>
                </b-col>
                <b-col :md="isBlockedState ? '3': '4'">
                  <b-form-group>
                    <label>Phone Number:</label>
                    <b-form-input
                      placeholder="Search"
                      type="text"
                      class="d-inline-block"
                      @input="(val) => (searchQuery.phoneNumber = val)"
                    />
                  </b-form-group>
                </b-col>
                <b-col :md="isBlockedState ? '3': '4'">
                  <b-form-group>
                    <label>National ID:</label>
                    <b-form-input
                      placeholder="Search"
                      type="text"
                      class="d-inline-block"
                      @input="(val) => (searchQuery.nationalId = val)"
                    />
                  </b-form-group>
                </b-col>
                <b-col
                  v-if="isBlockedState"
                  md="3"
                  class="d-flex justify-content-center align-items-center"
                >
                  <b-form-checkbox v-model="selfRegistered">
                    Self Registered
                  </b-form-checkbox>
                </b-col>

                <b-col>
                  <b-button
                    type="submit"
                    variant="primary"
                    @click="applyAdvancedSearch"
                  >
                    <span>Search</span>
                  </b-button>
                </b-col>
              </b-row>
            </b-form>
          </template>
        </card-collapse>

        <b-row>
          <b-col
            cols="12"
            md="9"
          >
            <b-row>
              <b-col md="12">
                <b-input-group>
                  <b-form-input
                    placeholder="Phone"
                    @keypress.enter="applyQuickSerach"
                    @input="(val) => (searchQuery.text = val)"
                  />
                  <b-form-input
                    placeholder="Device Id"
                    @keypress.enter="applyQuickSerach"
                    @input="(val) => (searchQuery.device_id = val)"
                  />
                  <b-form-input
                    placeholder="Car Number"
                    @keypress.enter="applyQuickSerach"
                    @input="(val) => (searchQuery.carNumber = val)"
                  />
                  <b-input-group-append>
                    <b-button
                      variant="outline-primary"
                      @click="applyQuickSerach"
                    >
                      Search
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-col>
            </b-row>
          </b-col>
          <b-col
            cols="6"
            class="mt-2 mt-md-0 d-flex justify-content-center"
            md="2"
          >
            <b-button
              v-if="$can('write', 'drivers') && !isBlockedState"
              variant="primary"
              :to="{ name: 'drivers-new' }"
            >
              <span class="text-nowrap">Add Driver</span>
            </b-button>
            <b-button
              class="btn-icon rounded-circle ml-1"
              variant="flat-primary"
              @click="fetchDrivers"
            >
              <feather-icon icon="RefreshCwIcon" />
            </b-button>
          </b-col>
          <b-col :class="`d-flex ${$can('read', 'drivers-license') ? 'justify-content-center' : 'justify-content-end'} align-items-center`">
            <h6>Total: {{ totalDrivers }}</h6>
          </b-col>
        </b-row>
      </div>

      <b-overlay
        variant="white"
        spinner-variant="primary"
        blur="0"
        opacity=".75"
        rounded="sm"
        :show="loadingDrivers"
      >
        <b-table
          ref="refDriverListTable"
          class="position-relative text-center"
          :items="drivers"
          responsive
          :fields="tableColumns"
          primary-key="id"
          :sort-by.sync="sortBy"
          show-empty
          empty-text="In order to get results you need to search for specific data."
          :sort-desc.sync="isSortDirDesc"
          :style="totalDrivers === 1 ? 'height :150px' : 'height: auto'"
        >
          <!-- Column: Driver -->
          <template #cell(driver)="data">
            <b-media vertical-align="center">
              <b-link
                v-if="data.item.id && data.item.externalId"
                :to="{
                  name: 'drivers-view',
                  params: { id: data.item.id },
                  query: { externalId: data.item.externalId },
                }"
                class="font-weight-bold d-block text-nowrap"
              >
                {{ data.item.fullName }}
              </b-link>
              <span v-else>
                {{ data.item.fullName }}
              </span>
              <small
                v-if="data.item.driverId"
                class="text-muted"
              >@{{ data.item.driverId }}</small>
            </b-media>
          </template>

          <!-- Column: Status -->
          <template #cell(status)="data">
            <b-badge
              v-if="data.item.activeState"
              pill
              :variant="
                `light-${resolveDriverActiveStateVariant(
                  data.item.activeState
                )}`
              "
              class="text-capitalize"
            >
              {{
                handleStatusLabel(data.item)
              }}
            </b-badge>
          </template>
          <template #cell(isLicensed)="data">
            <b-badge
              pill
              :variant="`light-${data.item.isLicensed ? 'success' : 'danger'}`"
              class="text-capitalize"
            >{{ data.item.isLicensed ? "Yes" : "No" }}</b-badge>
          </template>

          <!-- Column: AcceptanceRatio -->
          <template #cell(acceptanceRatio)="data">
            {{
              !data.item.acceptanceRatio || data.item.acceptanceRatio == "None"
                ? "100"
                : data.item.acceptanceRatio
            }}
            %
          </template>

          <!-- Column: Actions -->
          <template #cell(actions)="data">
            <b-dropdown
              variant="link"
              no-caret
              :right="$store.state.appConfig.isRTL"
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>
              <b-dropdown-item
                v-if="!isBlockedState && data.item.id && data.item.externalId"
                :to="{
                  name: 'drivers-view',
                  params: { id: data.item.id },
                  query: { externalId: data.item.externalId },
                }"
              >
                <feather-icon icon="FileTextIcon" />
                <span class="align-middle ml-50">Details</span>
              </b-dropdown-item>

              <b-dropdown-item
                v-if="
                  $can('write', 'drivers') &&
                    !isBlockedState &&
                    data.item.id &&
                    data.item.externalId
                "
                :to="{ name: 'drivers-edit', params: { id: data.item.id } }"
              >
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50">Edit</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="$can('write', 'drivers') && isBlockedState"
                @click="approveDriver(data.item)"
              >
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50">Approve</span>
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
      </b-overlay>
      <div class="mx-2 mb-2">
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="8"
            md="6"
            lg="6"
          >
            <div
              class="d-flex align-items-center align-items-lg-center  mb-1 mb-md-0"
            >
              <label>Show</label>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block mx-sm-1"
              />
              <label>entries</label>
            </div>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalDrivers"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BFormGroup,
  BButton,
  BTable,
  BMedia,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BInputGroupAppend,
  BInputGroup,
  BOverlay,
  BForm,
  BFormCheckbox,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { onUnmounted } from '@vue/composition-api'
import { avatarText } from '@core/utils/filter'
import store from '@/store'
import useDriversList from './useDriversList'
import driverStoreModule from '../driverStoreModule'
import CardCollapse from '@/UI/CardCollapse.vue'
import BulksModal from '@/views/billing/transaction-bulks/components/BulksModal.vue'
import inactivationPriority from '@/views/driver/inactivation-priority'
import { isJordanianAdmin } from '@/utils/validate-admin-country'
import BulkBlockDrivers from '../components/BulkBlockDrivers.vue'
import BulkClearInflightModal from '@/views/driver-user-shared/bulk-clear-inflight/BulkClearInflightModal.vue'

export default {
  components: {
    CardCollapse,
    BulkBlockDrivers,
    BCard,
    BRow,
    BCol,
    BForm,
    BFormInput,
    BFormCheckbox,
    BFormGroup,
    BButton,
    BTable,
    BMedia,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BInputGroupAppend,
    BulkClearInflightModal,
    BInputGroup,
    BOverlay,
    vSelect,
    BulksModal,
  },
  props: {
    isBlockedState: {
      type: Boolean,
      default: false,
    },
    initialFetchList: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    approveDriver(driver) {
      this.$swal({
        title: 'Are you sure?',
        text: `You want to approve ${driver.fullName}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Approve',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.$store
            .dispatch('drivers/updateDriver', {
              id: driver.id,
              data: { activeState: 'ACTIVE' },
            })
            .then(() => {
              this.fetchDrivers()
              this.$swal({
                icon: 'success',
                title: 'Approved!',
                text: `${driver.fullName} is Approved`,
                customClass: {
                  confirmButton: 'btn btn-success',
                },
              })
            })
        }
      })
    },
  },
  setup(props, ctx) {
    const DRIVER_APP_STORE_MODULE_NAME = 'drivers'

    // Register module
    if (!store.hasModule(DRIVER_APP_STORE_MODULE_NAME)) { store.registerModule(DRIVER_APP_STORE_MODULE_NAME, driverStoreModule) }

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(DRIVER_APP_STORE_MODULE_NAME)) { store.unregisterModule(DRIVER_APP_STORE_MODULE_NAME) }
    })

    const statusOptions = {
      ACTIVE: { label: 'Approved' },
      INACTIVE: { label: 'Blocked' },
    }
    const handleStatusLabel = driver => {
      if (driver.activeState === 'ACTIVE') return 'Approved'
      return inactivationPriority(driver.inactivationReasons)
    }
    const {
      fetchDrivers,
      drivers,
      quickFilterApplied,
      advancedFilterApplied,
      tableColumns,
      perPage,
      currentPage,
      totalDrivers,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refDriverListTable,
      selfRegistered,
      loadingDrivers,

      // UI
      resolveDriverActiveStateVariant,
    } = useDriversList(props, ctx)

    const applyQuickSerach = () => {
      quickFilterApplied.value = true
      advancedFilterApplied.value = false
      fetchDrivers()
    }
    const applyAdvancedSearch = () => {
      quickFilterApplied.value = false
      advancedFilterApplied.value = true
      fetchDrivers()
    }
    if (props.initialFetchList) {
      fetchDrivers()
    }

    return {
      fetchDrivers,
      drivers,
      handleStatusLabel,
      selfRegistered,
      tableColumns,
      perPage,
      currentPage,
      totalDrivers,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refDriverListTable,
      loadingDrivers,
      applyQuickSerach,
      applyAdvancedSearch,

      // Filter
      avatarText,

      // UI
      resolveDriverActiveStateVariant,

      statusOptions,

      isJordanianAdmin,
    }
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
::v-deep .list-inline svg {
  color: #d84e71;
  width: 2rem !important;
  height: 2rem !important;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-sweetalert.scss";
</style>
